import { combineEpics, Epic } from 'redux-observable';
import { from, of, EMPTY } from 'rxjs';
import {
  delay,
  switchMap,
  map,
  mergeMap,
  startWith,
  catchError,
  filter,
} from 'rxjs/operators';
import { isOfType } from 'typesafe-actions';
import { Intent, Position, Toaster } from '@blueprintjs/core';

import { IState } from '../reducers';
import {
  BlockResultActionsTypes,
  BlockResultActions,
  executingBlock,
  executingBlockFailed,
  loadingBlockResults,
  loadedBlockResults,
  loadingBlockResultsFailed,
  checkBlockExecution,
  addBlockResult,
  updateBlockResult,
  executedBlock,
  stoppingSqlBlockExecution,
  stoppingSqlBlockExecutionFailed,
  stoppedSqlBlockExecution,
} from '../actions/blockResultActions';
import {setShowSubscriptionDialog} from '../actions/uiActions';
import {
  getBlockResult,
  getBlockResults,
  executeBlock,
  stopBlockExecution,
} from '../../services/blockApi';

const blockResultToaster = Toaster.create({
  className: 'block-result-toaster',
  position: Position.TOP
});

const loadBlockResultsEpic: Epic<BlockResultActions, BlockResultActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(BlockResultActionsTypes.LOAD_BLOCK_RESULTS)),
    switchMap((action: any) =>
      from(getBlockResults(
        action.payload,
      )
      ).pipe(
          map((results: any) => {
            return loadedBlockResults(results);
          }),
          startWith(loadingBlockResults()),
          catchError((err: any) => of(loadingBlockResultsFailed()))
        )
    )
  );

const executeBlockEpic: Epic<BlockResultActions, BlockResultActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(BlockResultActionsTypes.EXECUTE_BLOCK)),
    switchMap((action: any) =>
      from(executeBlock(
        action.payload.uid,
        action.payload.reportUid,
        action.payload.block
        )
      ).pipe(
        map(({run_id: resultId}: any) => {
          /*
           * after successful execution, check for result
           */
          return checkBlockExecution(
            action.payload.uid,
            resultId,
          );
        }),
        startWith(executingBlock()),
        catchError((err: any) => {
          const message = err.response?.data?.message || "";

          if (message.indexOf("No subscription found") >= 0) {
            dispatch(setShowSubscriptionDialog(true));
          } else {
            blockResultToaster.show({
              message: `An error occurred while executing: ${message}.`,
              intent: Intent.DANGER
            });
          }

          return of(executingBlockFailed(action.payload.uid));
        })
      )
    )
  );

const stopBlockExecutionEpic: Epic<BlockResultActions, BlockResultActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) => {
  return action$.pipe(
    filter(isOfType(BlockResultActionsTypes.STOP_SQL_BLOCK_EXECUTION)),
    switchMap((action: any) =>
      from(
        stopBlockExecution(action.payload.uid, action.payload.resultId)
      ).pipe(
        map((result: any) => {
          return stoppedSqlBlockExecution(action.payload.uid, action.payload.resultId)
        }),
        startWith(stoppingSqlBlockExecution()),
        catchError((err: any) => {
          const message = err.response?.data?.message || "";

          blockResultToaster.show({
            message: `An error occurred while stopping block execution: ${message}.`,
            intent: Intent.DANGER
          });

          return of(stoppingSqlBlockExecutionFailed());
        })
      )
    )
  );
}

const CHECK_FINISH_STATUS = ['success', 'failed', 'cancelled']

const executionIsFinished = (result?: any): boolean =>
  !!(result && result.status && CHECK_FINISH_STATUS.indexOf(result.status) >= 0)

const checkBlockExecutionEpic: Epic<BlockResultActions, BlockResultActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(BlockResultActionsTypes.CHECK_BLOCK_EXECUTION)),
    mergeMap(
      (action: any) => {
        /*
         * @TODO - review
         */
        let delayTime = 1000;

        if (action.payload.counter < 2) {
          delayTime = 250;
        } else if (action.payload.counter < 5) {
          delayTime = 500;
        } else if (action.payload.counter < 10) {
          delayTime = 750;
        }

        if (action.payload.counter > 0 && state$.value.blockResult.results === null) {
          /*
           * if block results were unset, no need to check for results anymore
           */
          return EMPTY;
        }

        return from(getBlockResult(action.payload.uid, action.payload.resultId))
          .pipe(
            delay(delayTime),
            map(({ result, last_results }: any) => {
              if (action.payload.counter >= 1800 || !result) { // 30min query limit
                /*
                 * if no execution or we checked more than N times => Timeout
                 */
                return dispatch(
                  executedBlock(action.payload.uid, action.payload.resultId, result),
                );
              } else if (action.payload.counter === 0) {
                /*
                 * if it's first time, set the execution object
                 */
                dispatch(addBlockResult(action.payload.uid, result.key, result));

                if (executionIsFinished(result)) {
                  /*
                   * It can happen that action.payload.counter equals zero, but we have a finished result
                   *   because it's an old execution, loading on page load
                   */
                  return executedBlock(action.payload.uid, action.payload.resultId, {
                    ...result,
                    last_results,
                  });
                }
                /**
                 * The first time the executedSql property can be null, so we need
                 * to pass the hint for next execution check. When the executedSql property
                 * becomes not null, we need to dispatch this hint to the store.
                 * FYI executedSql is used in the query details pop both during the execution
                 * and when the execution ended.
                 */
                return checkBlockExecution(
                  action.payload.uid,
                  action.payload.resultId,
                  action.payload.counter + 1);
              } else if (executionIsFinished(result)) {
                /*
                 * executed
                 */
                return executedBlock(action.payload.uid, action.payload.resultId, {
                  ...result,
                  last_results,
                });
              }

              return checkBlockExecution(
                action.payload.uid,
                action.payload.resultId,
                action.payload.counter += 1);
            }),
            catchError((err: any) => {
              const message = err.response?.data?.message || "";

              blockResultToaster.show({
                message: `An error occurred while checking for execution: ${message}.`,
                intent: Intent.DANGER
              });

              return of(executingBlockFailed(action.payload.uid, action.payload.resultId));
            }),
        )
      }
    )
  );

export default combineEpics(
  checkBlockExecutionEpic,
  executeBlockEpic,
  loadBlockResultsEpic,
  stopBlockExecutionEpic,
);
